import { useTranslation } from 'react-i18next';
import {
    Button,
    Callout,
    FormField,
    Input,
    Item,
    Modal,
    OptionCard,
    Segmented,
    Select,
} from '@/components/ui';
import type { ProvisionAccountVM } from './useProvisionAccount';
import styles from './ProvisionAccountModal.module.css';

// EAA-1846: the badge modifier is hashed now, so `sa-provision__badge--${badge}`
// can no longer be built from the value — this is the explicit lookup.
const BADGE_CLASS: Record<'in-branch' | 'no-level', string> = {
    'in-branch': styles.badgeInBranch,
    'no-level': styles.badgeNoLevel,
};

/**
 * EAA-1839 (frames 35e + 35f): the "Cấp tài khoản" modal — a 1000px-wide,
 * two-column form with a mode switch at the top, exactly as the design changelog
 * records it ("Hai frame 35e · 35f đổi từ drawer 472px sang modal giữa màn rộng
 * 960px" — the two frames are one modal in two modes, not two dialogs).
 *
 * `useProvisionAccount` owns the writes and documents every field the frames draw
 * that has no place to be written; each such control is rendered here, disabled,
 * carrying that reason.
 */
export function ProvisionAccountModal({ provision }: { provision: ProvisionAccountVM }) {
    const { t } = useTranslation();

    const levelOptions = provision.levels.map((level) => ({
        value: level.id,
        label: `${level.name} · ${level.role_key}`,
    }));

    let body: React.ReactNode;

    if (provision.mode === 'create') {
        body = (
            <div className={styles.grid}>
                <FormField
                    label={t('console.hierarchy.scopedAccounts.provision.fullNameLabel')}
                    required
                >
                    <Input
                        value={provision.createForm.fullName}
                        onChange={(event) => provision.setFullName(event.target.value)}
                        placeholder={t(
                            'console.hierarchy.scopedAccounts.provision.fullNamePlaceholder'
                        )}
                    />
                </FormField>

                <FormField
                    label={t('console.hierarchy.scopedAccounts.provision.usernameLabel')}
                    required
                    hint={t('console.hierarchy.scopedAccounts.provision.usernameHint')}
                    error={
                        provision.usernameTaken
                            ? t('console.hierarchy.scopedAccounts.provision.usernameTaken')
                            : undefined
                    }
                >
                    <Input
                        value={provision.createForm.username}
                        onChange={(event) => provision.setUsername(event.target.value)}
                        suffix={
                            provision.usernameChecking
                                ? t('console.hierarchy.scopedAccounts.provision.usernameChecking')
                                : undefined
                        }
                    />
                </FormField>

                <FormField label={t('console.hierarchy.scopedAccounts.provision.emailLabel')}>
                    <Input
                        type="email"
                        value={provision.createForm.email}
                        onChange={(event) => provision.patchCreate({ email: event.target.value })}
                    />
                </FormField>

                <FormField
                    label={t('console.hierarchy.scopedAccounts.provision.levelLabel', {
                        org: provision.orgName ?? '',
                    })}
                    hint={t('console.hierarchy.scopedAccounts.provision.levelHint')}
                >
                    <Select
                        allowClear
                        value={provision.createForm.levelId || undefined}
                        onChange={(value) => provision.patchCreate({ levelId: (value as string) ?? '' })}
                        options={levelOptions}
                        placeholder={t('console.hierarchy.scopedAccounts.provision.levelNone')}
                    />
                </FormField>

                <FormField
                    className={styles.wide}
                    label={t('console.hierarchy.scopedAccounts.provision.staffCodeLabel')}
                    hint={t('console.hierarchy.scopedAccounts.provision.staffCodeUnavailable')}
                >
                    <Input disabled placeholder="—" />
                </FormField>

                <fieldset className={`${styles.wide} ${styles.cards}`}>
                    <legend className={styles.cardsLegend}>
                        {t('console.hierarchy.scopedAccounts.provision.credentialLegend')}
                    </legend>
                    <OptionCard
                        name="sa-provision-credential"
                        value="invite"
                        selected={provision.createForm.credentialMode === 'invite'}
                        onSelect={() => provision.patchCreate({ credentialMode: 'invite' })}
                        title={t('console.hierarchy.scopedAccounts.provision.credentialInvite')}
                        description={t(
                            'console.hierarchy.scopedAccounts.provision.credentialInviteHint'
                        )}
                    />
                    <OptionCard
                        name="sa-provision-credential"
                        value="temp_password"
                        selected={provision.createForm.credentialMode === 'temp_password'}
                        onSelect={() => provision.patchCreate({ credentialMode: 'temp_password' })}
                        title={t('console.hierarchy.scopedAccounts.provision.credentialTemp')}
                        description={t(
                            'console.hierarchy.scopedAccounts.provision.credentialTempHint'
                        )}
                    />
                </fieldset>

                {/* EAA-1839: `className` directly on the Callout — it extends
                    `Styleable` now, so the grid-span wrapper it used to need is gone. */}
                <Callout className={styles.wide} tone="info" longLabel>
                    {t('console.hierarchy.scopedAccounts.provision.createNote')}
                </Callout>
            </div>
        );
    } else if (!provision.canSearchRealm) {
        body = (
            <Callout tone="warning" longLabel>
                {t('console.hierarchy.scopedAccounts.provision.searchNoPermission')}
            </Callout>
        );
    } else {
        const selected = provision.selected;
        body = (
            <div className={styles.existing}>
                <FormField
                    label={t('console.hierarchy.scopedAccounts.provision.searchLabel')}
                    required
                    hint={t('console.hierarchy.scopedAccounts.provision.searchHint')}
                >
                    <Input
                        value={provision.existingQuery}
                        onChange={(event) => provision.setExistingQuery(event.target.value)}
                        placeholder={t(
                            'console.hierarchy.scopedAccounts.provision.searchPlaceholder'
                        )}
                        allowClear
                    />
                </FormField>

                <ul className={styles.results}>
                    {provision.searchResults.map((row) => {
                        const badge = provision.badgeFor(row);
                        return (
                            <li key={row.sub}>
                                <Item
                                    selected={selected?.sub === row.sub}
                                    onClick={() => provision.setSelected(row)}
                                    title={row.username}
                                    caption={row.email || row.sub}
                                    trailing={
                                        <span className={styles.resultMeta}>
                                            <span className={styles.resultPlacement}>
                                                {row.placement
                                                    ? `${row.placement.organization_name} · ${row.placement.level_name}`
                                                    : t(
                                                          'console.hierarchy.scopedAccounts.provision.noPlacement'
                                                      )}
                                            </span>
                                            {badge && (
                                                <span
                                                    className={`${styles.badge} ${BADGE_CLASS[badge]}`}
                                                >
                                                    {t(
                                                        `console.hierarchy.scopedAccounts.provision.badge_${badge}`
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                    }
                                />
                            </li>
                        );
                    })}
                    {provision.searching && (
                        <li className={styles.resultsNote}>
                            {t('common.loading')}
                        </li>
                    )}
                    {!provision.searching &&
                        provision.existingQuery.trim().length >= 2 &&
                        provision.searchResults.length === 0 && (
                            <li className={styles.resultsNote}>
                                {t('console.hierarchy.scopedAccounts.provision.searchNoResults')}
                            </li>
                        )}
                </ul>

                <div className={styles.grid}>
                    <FormField
                        label={t('console.hierarchy.scopedAccounts.provision.targetOrgLabel')}
                        hint={t('console.hierarchy.scopedAccounts.provision.targetOrgHint')}
                    >
                        <Input value={provision.orgName ?? ''} readOnly />
                    </FormField>

                    <FormField
                        label={t('console.hierarchy.scopedAccounts.provision.levelLabel', {
                            org: provision.orgName ?? '',
                        })}
                        required
                    >
                        <Select
                            value={provision.existingLevelId || undefined}
                            onChange={(value) => provision.setExistingLevelId(value as string)}
                            options={levelOptions}
                            placeholder={t('console.hierarchy.scopedAccounts.provision.levelPick')}
                        />
                    </FormField>

                    <fieldset className={`${styles.wide} ${styles.cards}`}>
                        <legend className={styles.cardsLegend}>
                            {t('console.hierarchy.scopedAccounts.provision.attachLegend')}
                        </legend>
                        <OptionCard
                            name="sa-provision-attach"
                            value="secondary"
                            selected
                            onSelect={() => undefined}
                            title={t('console.hierarchy.scopedAccounts.provision.attachSecondary')}
                            description={t(
                                'console.hierarchy.scopedAccounts.provision.attachSecondaryHint'
                            )}
                        />
                        <OptionCard
                            name="sa-provision-attach"
                            value="primary"
                            selected={false}
                            onSelect={() => undefined}
                            disabled
                            disabledReason={t(
                                'console.hierarchy.scopedAccounts.provision.attachPrimaryUnavailable'
                            )}
                            title={t('console.hierarchy.scopedAccounts.provision.attachPrimary')}
                            description={t(
                                'console.hierarchy.scopedAccounts.provision.attachPrimaryHint'
                            )}
                        />
                    </fieldset>

                    <FormField
                        className={styles.wide}
                        label={t('console.hierarchy.scopedAccounts.provision.effectiveLabel')}
                        hint={t('console.hierarchy.scopedAccounts.provision.effectiveUnavailable')}
                    >
                        <Input disabled placeholder="—" />
                    </FormField>
                </div>

                <Callout tone="warning" longLabel>
                    {t('console.hierarchy.scopedAccounts.provision.scopeWarning')}
                </Callout>
            </div>
        );
    }

    return (
        <>
            <Modal
                open={provision.open}
                onCancel={provision.closeModal}
                width={1000}
                className="sa-provision"
                title={t('console.hierarchy.scopedAccounts.provision.title')}
                footer={
                    <>
                        <Button onClick={provision.closeModal} disabled={provision.busy}>
                            {t('common.cancel')}
                        </Button>
                        <Button type="primary" loading={provision.busy} onClick={provision.submit}>
                            {provision.mode === 'create'
                                ? t('console.hierarchy.scopedAccounts.provision.createSubmit')
                                : t('console.hierarchy.scopedAccounts.provision.attachSubmit')}
                        </Button>
                    </>
                }
            >
                <p className={styles.lead}>
                    {t('console.hierarchy.scopedAccounts.provision.lead', {
                        org: provision.orgName ?? '',
                    })}
                </p>

                <Segmented
                    className={styles.mode}
                    block
                    value={provision.mode}
                    onChange={(value) => provision.setMode(value as ProvisionAccountVM['mode'])}
                    options={[
                        {
                            value: 'create',
                            label: t('console.hierarchy.scopedAccounts.provision.modeCreate'),
                            disabled: !provision.canIssue,
                            title: provision.canIssue
                                ? undefined
                                : t('console.hierarchy.scopedAccounts.provision.modeCreateNoPermission'),
                        },
                        {
                            value: 'existing',
                            label: t('console.hierarchy.scopedAccounts.provision.modeExisting'),
                        },
                    ]}
                />

                {provision.error && (
                    <Callout tone="danger" longLabel>
                        {provision.error}
                    </Callout>
                )}

                {body}
            </Modal>

            {provision.issued?.temp_password && (
                <div className="portal-admin-secret" role="alert">
                    <strong>
                        {t('console.hierarchy.scopedAccounts.provision.secretHeading')}
                    </strong>
                    <span>
                        {t('console.hierarchy.scopedAccounts.dialogs.usernameLabel')}{' '}
                        <code>{provision.issued.username}</code>
                    </span>
                    <span>
                        {t('console.hierarchy.scopedAccounts.dialogs.tempPasswordLabel')}{' '}
                        <code>{provision.issued.temp_password}</code>
                    </span>
                    <Button onClick={provision.dismissIssued}>
                        {t('console.hierarchy.scopedAccounts.dialogs.dismissResetResult')}
                    </Button>
                </div>
            )}
        </>
    );
}

export default ProvisionAccountModal;
